import { MagnifyingGlassIcon } from "@heroicons/react/20/solid";
import { ColumnDef, createColumnHelper } from "@tanstack/react-table";
import { GenericError } from "@via/components/error.tsx";
import TagsCell from "@via/components/tags-cell.tsx";
import Table, { TableSize } from "@via/components/table/table";
import { TruncatedText } from "@via/components/truncated-text.tsx";
import useUrlParams from "@via/hooks/useURLParams";
import { useActivityRunList } from "@via/pages/activity/activity.api";
import RunPipelineLink from "@via/pages/projects/runs/run-pipeline-link";
import { getRunStatusLabel } from "@via/pages/projects/runs/utils/helpers";
import RunsAdvancedFilter from "@via/pages/runs/components/runs-advanced-filter";
import { FILTER_FIELD_OPTIONS } from "@via/hooks/runs-filter-types.ts";
import { useRunsAdvancedFilter } from "@via/hooks/useRunsAdvancedFilter.ts";
import { EMPTY_TABLE_MESSAGE, ERROR } from "@via/utils/constants";
import { formatDateTimeTwoLines } from "@via/utils/helpers";
import { ActivityRunResponse } from "@via/utils/interfaces";
import { type JSX, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useDebounceCallback, useDebounceValue } from "usehooks-ts";

const defaultParams = {
  sort: [{ id: "dateCreatedLastRun", desc: true }],
  page: 0,
  pageSize: 20,
};

const runColumnHelper = createColumnHelper<ActivityRunResponse>();

export default function RunsList(): JSX.Element {
  const { setUrlParams, getInitialParams } = useUrlParams(defaultParams);
  const initialParams = getInitialParams();

  const [page, setPage] = useState(initialParams.page);
  const [pageSize, setPageSize] = useState(initialParams.pageSize);
  const [sort, setSort] = useState<Array<{ id: string; desc: boolean }>>(
    initialParams.sort
  );

  const [searchKey, setSearchKey] = useState("");
  const [debouncedSearchKey, setDebouncedSearchKey] = useDebounceValue(
    searchKey,
    300
  );

  const {
    isOpen: isFilterOpen,
    setIsOpen: setFilterOpen,
    openPanel: openFilterPanel,
    criteria,
    addCriteria,
    removeCriteria,
    updateCriteria,
    applyFilters,
    clearAll: clearFilters,
    hasActiveCriteria,
    appliedFilters,
  } = useRunsAdvancedFilter();

  const {
    mutate: fetchRuns,
    data: runsData,
    isPending: isRunsPending,
    isError: isRunsError,
  } = useActivityRunList();

  useEffect(() => {
    setUrlParams({ page, pageSize, sort });
    fetchRuns({
      page,
      pageSize,
      sort,
      searchKey,
      filter: appliedFilters,
    });
  }, [page, pageSize, sort, searchKey, appliedFilters]);

  const debounced = useDebounceCallback((value: string) => {
    setDebouncedSearchKey(value);
    setSearchKey(value);
    setPage(0);
    setPageSize(defaultParams.pageSize);
  }, 300);

  const handleApplyFilters = () => {
    applyFilters();
    setPage(0);
  };

  const handleClearFilters = () => {
    clearFilters();
    setPage(0);
  };

  const columns = [
    runColumnHelper.accessor("id", {
      size: 70,
      minSize: 60,
      maxSize: 90,
      cell: ({ row }) => (
        <Link to={`/runs/${row.original.id}`} className="via-link truncate">
          {row.original.id}
        </Link>
      ),
      header: () => <span>Run ID</span>,
    }),
    runColumnHelper.accessor("name", {
      size: 220,
      minSize: 160,
      maxSize: 420,
      cell: ({ row }) => (
        <Link to={`/runs/${row.original.id}`} className="via-link truncate">
          <TruncatedText
            text={row.original.name}
            maxLength={40}
            id={`runs-name-tooltip-${row.original.id}-${row.original.name}`}
          />
        </Link>
      ),
      header: () => <span>Run Name</span>,
    }),
    runColumnHelper.accessor("status", {
      size: 180,
      minSize: 150,
      cell: ({ row }) => (
        <div className="flex items-center gap-2">
          {getRunStatusLabel(row.original.status)}
        </div>
      ),
      header: () => <span>Status</span>,
    }),
    runColumnHelper.accessor("pipelineName", {
      size: 220,
      minSize: 160,
      maxSize: 320,
      // A standalone process run's pipelineId is a synthesized one node pipeline, and the
      // pipeline editor refuses to open it, so this cell must not link there. RunPipelineLink
      // owns that rule for every surface that renders a run's pipeline reference; do not
      // re-inline a <Link> here.
      cell: ({ row }) => (
        <RunPipelineLink
          pipelineId={row.original.pipelineId}
          pipelineName={row.original.pipelineName}
          subjectType={row.original.subjectType}
          sourceProcessId={row.original.sourceProcessId}
        />
      ),
      header: () => <span>Pipeline</span>,
    }),
    runColumnHelper.accessor("pipelineVersion", {
      size: 90,
      minSize: 70,
      maxSize: 120,
      cell: ({ row }) => (
        <span className="truncate">{row.original.pipelineVersion}</span>
      ),
      header: () => <span>Version</span>,
      enableSorting: false,
    }),
    runColumnHelper.accessor("username", {
      size: 170,
      minSize: 130,
      maxSize: 220,
      cell: ({ row }) => (
        <span className="truncate">{row.original.username}</span>
      ),
      header: () => <span>Owner</span>,
    }),
    runColumnHelper.display({
      id: "tags",
      size: 180,
      minSize: 120,
      maxSize: 300,
      cell: ({ row }) => (
        <TagsCell tags={row.original.tags ?? []} rowId={row.original.id} />
      ),
      header: () => <span>Tags</span>,
      enableSorting: false,
    }),
    runColumnHelper.accessor("dateCreated", {
      size: 150,
      minSize: 120,
      maxSize: 200,
      cell: ({ row }) => formatDateTimeTwoLines(row.original.dateCreated),
      header: () => <span>Date Created</span>,
    }),
    runColumnHelper.accessor("dateCreatedLastRun", {
      size: 150,
      minSize: 120,
      maxSize: 200,
      cell: ({ row }) =>
        formatDateTimeTwoLines(row.original.dateCreatedLastRun),
      sortDescFirst: true,
      header: () => <span>Last Run</span>,
    }),
  ] as Array<ColumnDef<ActivityRunResponse>>;

  if (isRunsError) {
    return (
      <div className="via-generic-error">
        <GenericError message={ERROR} />
      </div>
    );
  }

  return (
    <Table
      isLoading={isRunsPending}
      data={runsData?.data}
      columns={columns}
      size={TableSize.Big}
      showPagination={true}
      page={page}
      sort={sort}
      setSort={setSort}
      setPage={setPage}
      pageSize={pageSize}
      setPageSize={setPageSize}
      emptyMessage={EMPTY_TABLE_MESSAGE.RUNS}
      total={runsData?.total}
      applyColumnSize
      additionalFilters={
        <div className="mt-4 flex-grow">
          <div className="relative">
            <MagnifyingGlassIcon
              className="pointer-events-none absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-gray-400"
              aria-hidden="true"
            />
            <input
              type="text"
              placeholder="Search across all fields..."
              className="w-full rounded-md border-0 py-2 pl-10 pr-3 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
              defaultValue={debouncedSearchKey}
              onChange={e => debounced(e.target.value)}
            />
          </div>
          <RunsAdvancedFilter
            isOpen={isFilterOpen}
            onToggle={() =>
              isFilterOpen ? setFilterOpen(false) : openFilterPanel()
            }
            criteria={criteria}
            fieldOptions={FILTER_FIELD_OPTIONS}
            onAddCriteria={addCriteria}
            onRemoveCriteria={removeCriteria}
            onUpdateCriteria={updateCriteria}
            onApply={handleApplyFilters}
            onClear={handleClearFilters}
            hasActiveCriteria={hasActiveCriteria}
          />
        </div>
      }
    />
  );
}
